import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, SimpleGrid } from '@chakra-ui/react';
import { MdDownload } from 'react-icons/md';
import { Button as ButtonComponent, DialogComponent } from '@themes/aegix';
import { toaster } from '../../ui/toaster';
import {
  getIncidentByFilterPDF,
  getIncidentByFilterCSV,
  getIncidentByFilterJSON,
  type IncidentExportPayload,
} from '../../../services/alerts.service';

interface AlertHistoryExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  payload: IncidentExportPayload;
}

type ExportFormat = 'pdf' | 'csv' | 'json';

export function AlertHistoryExportDialog({
  isOpen,
  onClose,
  payload,
}: AlertHistoryExportDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<Record<ExportFormat, boolean>>({
    pdf: false,
    csv: false,
    json: false,
  });

  const exporters: Record<
    ExportFormat,
    (p: IncidentExportPayload) => Promise<boolean>
  > = {
    pdf: getIncidentByFilterPDF,
    csv: getIncidentByFilterCSV,
    json: getIncidentByFilterJSON,
  };

  const handleExport = async (format: ExportFormat) => {
    setLoading((prev) => ({ ...prev, [format]: true }));
    try {
      const ok = await exporters[format](payload);
      if (!ok) {
        toaster.create({
          title: t('events.exportEmpty', 'Export was empty'),
          description: t('events.exportEmptyDesc', 'The server returned no data for this export.'),
          type: 'warning',
        });
      } else {
        onClose();
      }
    } catch (err) {
      toaster.create({
        title: t('events.fileDownloadFailed', 'File download failed'),
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setLoading((prev) => ({ ...prev, [format]: false }));
    }
  };

  const anyLoading = loading.pdf || loading.csv || loading.json;

  return (
    <DialogComponent
      isOpen={isOpen}
      onClose={onClose}
      e2eTitle="alertHistoryExportDialog"
      headingText={t('events.exportEventDetail', 'Export Event Detail')}
      size="md"
    >
      <Box p={4}>
        <SimpleGrid columns={3} gap={2}>
          {(['pdf', 'csv', 'json'] as const).map((format) => (
            <ButtonComponent
              key={format}
              visual="primary"
              onClick={() => handleExport(format)}
              disabled={anyLoading}
              css={{ gap: '8px' }}
            >
              <Box fontSize="lg">
                <MdDownload />
              </Box>
              {format.toUpperCase()}
            </ButtonComponent>
          ))}
        </SimpleGrid>
      </Box>
    </DialogComponent>
  );
}
